/**
 * Shared vCard generation and download (iOS Contacts preview, Android importer, desktop).
 * Used by DisplayCardPage (public card), useContactActions, and ContactsPage download.
 */

import { trackCardAction } from './analytics';

/**
 * Strip control characters and escape vCard special chars (RFC 2426).
 */
export function escapeVCardValue(value) {
  if (!value) return '';
  return value
    .toString()
    // Matching control characters is the entire purpose of this sanitiser:
    // unescaped ones corrupt the vCard and allow field injection into the .vcf.
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/[\\,;]/g, '\\$&');
}

/** Allow only http(s) URLs for URL: lines (no javascript:, data:, etc.). */
export function sanitizeHttpUrlForVCard(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const t = raw.trim();
  if (!t) return '';
  const href = /^https?:\/\//i.test(t) ? t : `https://${t}`;
  try {
    const u = new URL(href);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    return u.href;
  } catch {
    return '';
  }
}

/**
 * Build vCard 3.0 string from a contact-like object (saved contact or public cardInfo).
 */
export function generateVCard(contact) {
  if (!contact) return '';
  const CRLF = '\r\n';
  let vCard = `BEGIN:VCARD${CRLF}VERSION:3.0${CRLF}`;

  vCard += `FN:${escapeVCardValue(contact.name)}${CRLF}`;
  vCard += `N:${escapeVCardValue(contact.name)};;;;${CRLF}`;

  if (contact.email) {
    vCard += `EMAIL;TYPE=INTERNET:${escapeVCardValue(contact.email)}${CRLF}`;
  }
  if (contact.phone) {
    vCard += `TEL;TYPE=CELL:${escapeVCardValue(contact.phone)}${CRLF}`;
  }
  if (contact.company) {
    vCard += `ORG:${escapeVCardValue(contact.company)}${CRLF}`;
  }
  if (contact.title) {
    vCard += `TITLE:${escapeVCardValue(contact.title)}${CRLF}`;
  }
  if (contact.address) {
    vCard += `ADR;TYPE=WORK:;;${escapeVCardValue(contact.address)};;;;;${CRLF}`;
  }

  const webUrl = sanitizeHttpUrlForVCard(contact.additionalURL);
  const linkedInUrl = sanitizeHttpUrlForVCard(contact.linkedinURL);
  if (webUrl) {
    vCard += `URL:${escapeVCardValue(webUrl)}${CRLF}`;
  }
  if (linkedInUrl && linkedInUrl !== webUrl) {
    vCard += `URL;TYPE=LinkedIn:${escapeVCardValue(linkedInUrl)}${CRLF}`;
  }

  const noteParts = [];
  if (contact.notes) noteParts.push(contact.notes);
  if (contact.role) noteParts.push(`Role: ${contact.role}`);
  if (contact.linkedinURL && !linkedInUrl) noteParts.push(`LinkedIn: ${contact.linkedinURL}`);
  if (contact.additionalURL && !webUrl) noteParts.push(`Web: ${contact.additionalURL}`);
  if (noteParts.length) {
    vCard += `NOTE:${escapeVCardValue(noteParts.join(' | '))}${CRLF}`;
  }

  vCard += `END:VCARD${CRLF}`;
  return vCard;
}

export function vCardFilename(name) {
  return (name || 'contact').replace(/[^\w\- ]+/g, '').trim() || 'contact';
}

const noop = () => {};

/**
 * Trigger .vcf download/open via Blob URL (iOS opens Contacts preview; Android importer; desktop save).
 */
function triggerVCardBlobDownload(vCard, baseName) {
  const blob = new Blob([vCard], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${baseName}.vcf`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    if (document.body.contains(link)) document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 1000);
}

/** Data-URI fallback when blob download is blocked (rare). */
function triggerVCardDataUriDownload(vCard, baseName) {
  const encodedVCard = encodeURIComponent(vCard);
  const dataUri = `data:text/vcard;charset=utf-8,${encodedVCard}`;
  const link = document.createElement('a');
  link.href = dataUri;
  link.download = `${baseName}.vcf`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    if (document.body.contains(link)) document.body.removeChild(link);
  }, 1000);
}

/**
 * Download .vcf — no Web Share / share sheet; native Contacts flow on mobile where supported.
 * @param {object} contact - { name, email, phone, company, title, role, notes, address, linkedinURL, additionalURL }
 * @param {{ showSuccess?: function, showError?: function }} handlers
 * @param {{ trackId?: string|null, trackAction?: boolean }} analytics - trackAction default true for save_as_contact
 * @returns {Promise<{ ok: boolean, aborted?: boolean }>}
 */
export async function shareOrDownloadVCard(contact, handlers = {}, analytics = {}) {
  const showSuccess = handlers.showSuccess || noop;
  const showError = handlers.showError || noop;
  const { trackId = null, trackAction: shouldTrack = true } = analytics;

  if (!contact || !String(contact.name || '').trim()) {
    showError('Contact name is required to create a card file.');
    return { ok: false };
  }

  const vCard = generateVCard({ ...contact, name: String(contact.name).trim() });
  const baseName = vCardFilename(contact.name);

  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMacOS = /Macintosh/.test(navigator.userAgent) && !isIOS;

  try {
    try {
      triggerVCardBlobDownload(vCard, baseName);
    } catch {
      triggerVCardDataUriDownload(vCard, baseName);
    }

    if (isIOS) {
      showSuccess('Opening Contacts…');
    } else if (isAndroid) {
      showSuccess('Opening Contacts…');
    } else if (isMacOS) {
      showSuccess('Contact file downloaded. Double-click to add to Contacts.');
    } else {
      showSuccess('Contact file downloaded. Import it to add to Contacts.');
    }

    if (shouldTrack) {
      trackCardAction('save_as_contact', trackId);
    }
    return { ok: true };
  } catch (error) {
    console.error('vCard download failed');
    showError('Failed to save contact to device contacts');
    return { ok: false };
  }
}
